import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LandingIntro from "../components/LandingIntro";

const INTRO_SEEN_KEY = "pys_intro_seen_v1";

const Cloud = ({ className, style, scale = 1, opacity = 1 }) => (
  <svg
    viewBox="0 0 200 100"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{ ...style, transform: `scale(${scale})`, opacity }}
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="cloudShade" cx="50%" cy="40%" r="60%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
        <stop offset="80%" stopColor="#eaf3ff" stopOpacity="1" />
        <stop offset="100%" stopColor="#cfe2ff" stopOpacity="0.9" />
      </radialGradient>
    </defs>
    <g fill="url(#cloudShade)">
      <ellipse cx="60" cy="60" rx="40" ry="28" />
      <ellipse cx="100" cy="50" rx="48" ry="34" />
      <ellipse cx="145" cy="62" rx="38" ry="26" />
      <ellipse cx="85" cy="70" rx="34" ry="22" />
      <ellipse cx="125" cy="72" rx="32" ry="20" />
    </g>
  </svg>
);

export default function Landing() {
  const [mounted, setMounted] = useState(false);
  const [introOpen, setIntroOpen] = useState(() => {
    try { return !sessionStorage.getItem(INTRO_SEEN_KEY); }
    catch (_) { return true; }
  });
  // Phase VV.13 — already-signed-in users see "Open feed" instead of the
  // first-time Google sign-in label. We do a single best-effort `/auth/me`
  // probe so the CTA matches reality without showing a separate sign-in
  // page in front of returning users.
  const [authed, setAuthed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          { credentials: "include" }
        );
        if (!cancelled && r.ok) {
          setAuthed(true);
          // Phase VV.44 — when an authenticated user opens the PWA (or
          // hits `/` directly while signed in), skip the marketing
          // landing and drop them straight into the feed. Without this,
          // the PWA always launches to "Get started" which feels like
          // it's bouncing them back to login.
          const isPwa =
            typeof window !== "undefined" &&
            (window.matchMedia?.("(display-mode: standalone)").matches ||
              navigator.standalone ||
              new URLSearchParams(window.location.search).get("source") === "pwa");
          if (isPwa) {
            navigate("/feed", { replace: true });
          }
        }
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [navigate]);

  const closeIntro = () => {
    setIntroOpen(false);
    try { sessionStorage.setItem(INTRO_SEEN_KEY, "1"); }
    catch (_) {}
  };

  return (
    <main
      data-testid="landing-root"
      className="relative min-h-screen w-full overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #6cb8ff 0%, #8fcaff 45%, #b8dcff 100%)",
        fontFamily:
          "'Space Grotesk', 'Manrope', 'Inter', system-ui, sans-serif",
      }}
    >
      {introOpen && <LandingIntro onDone={closeIntro} />}

      {/* sun glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-32 h-[480px] w-[480px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,240,0.55) 0%, rgba(255,255,240,0) 65%)",
          filter: "blur(8px)",
        }}
      />

      {/* drifting clouds */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute drift-slow" style={{ top: "12%", left: "-15%", width: "320px" }}>
          <Cloud opacity={0.95} />
        </div>
        <div className="absolute drift-medium" style={{ top: "26%", left: "55%", width: "260px" }}>
          <Cloud opacity={0.9} scale={0.9} />
        </div>
        <div className="absolute drift-slowest" style={{ top: "62%", left: "8%", width: "380px" }}>
          <Cloud opacity={0.85} />
        </div>
        <div className="absolute drift-medium" style={{ top: "74%", left: "62%", width: "300px" }}>
          <Cloud opacity={0.92} scale={0.95} />
        </div>
        <div className="absolute drift-fast" style={{ top: "45%", left: "30%", width: "220px" }}>
          <Cloud opacity={0.7} scale={0.8} />
        </div>
      </div>

      {/* PYS rotating mark */}
      <section
        className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6 gap-12"
        style={{ perspective: "1200px" }}
      >
        <div
          data-testid="pys-mark"
          className={`pys-spin select-none transition-all duration-1000 ${
            mounted ? "opacity-100 scale-100" : "opacity-0 scale-90"
          }`}
          style={{
            color: "#06b6d4",
            fontFamily:
              "'Playfair Display', 'Bodoni Moda', 'Cormorant Garamond', Georgia, serif",
            fontWeight: 900,
            fontSize: "clamp(7rem, 22vw, 22rem)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
            textShadow: [
              "1px 1px 0 #d61775",
              "2px 2px 0 #c81269",
              "3px 3px 0 #b80f5f",
              "4px 4px 0 #a80c55",
              "5px 5px 0 #98094b",
              "6px 6px 0 #880741",
              "7px 7px 0 #780538",
              "8px 8px 0 #68042f",
              "9px 9px 0 #580327",
              "10px 10px 0 #480220",
              "11px 11px 0 #38011a",
              "12px 12px 0 #280114",
              "20px 28px 30px rgba(0,0,0,0.35)",
            ].join(", "),
          }}
        >
          PYS
        </div>

        <p
          data-testid="brand-tagline"
          className={`max-w-md text-center text-sm sm:text-base text-white/95 transition-all duration-700 delay-200 ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            textShadow: "0 1px 12px rgba(40, 1, 20, 0.25)",
            letterSpacing: "0.01em",
          }}
        >
          a tiny cyan-sky social space.<br />
          <span className="text-white/80">post · share · DM in realtime</span>
        </p>

        <div
          className={`flex flex-wrap items-center justify-center gap-4 transition-all duration-700 delay-300 ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {/* Phase VV.13 — single zero-friction CTA. Returning visitors
              with a valid session land on the feed; first-time visitors
              go straight to the Google one-tap (no "Register" page to
              click through first). */}
          <button
            data-testid="open-btn"
            onClick={() => {
              if (authed) {
                navigate("/feed");
              } else {
                // Phase VV.14 — /register shows the username/password
                // form + Google fallback. No more forced Google redirect.
                navigate("/register");
              }
            }}
            className="px-10 py-3.5 rounded-full text-white font-semibold tracking-wide shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            style={{
              background: "#06b6d4",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {authed ? "Open feed" : "Open PYS"}
          </button>
        </div>
      </section>

      <style>{`
        .pys-spin {
          animation: pys-rotate 12s linear infinite;
          transform-origin: center center;
          transform-style: preserve-3d;
          display: inline-block;
          will-change: transform;
        }

        @keyframes pys-rotate {
          0%   { transform: rotateX(0deg)   rotateY(0deg)   rotateZ(0deg); }
          100% { transform: rotateX(-360deg) rotateY(-360deg) rotateZ(-360deg); }
        }

        .drift-slow    { animation: drift 60s linear infinite; }
        .drift-slowest { animation: drift 90s linear infinite; }
        .drift-medium  { animation: drift 45s linear infinite; animation-delay: -12s; }
        .drift-fast    { animation: drift 35s linear infinite; animation-delay: -20s; }

        @keyframes drift {
          from { transform: translateX(0); }
          to   { transform: translateX(140vw); }
        }

        @media (prefers-reduced-motion: reduce) {
          .pys-spin, .drift-slow, .drift-slowest, .drift-medium, .drift-fast { animation: none; }
        }
      `}</style>

      {/* Legal footer — always visible on the landing page so reviewers
          (AdSense, Apple, Google Play) can find the privacy policy. */}
      <footer
        data-testid="landing-footer"
        className="absolute bottom-3 left-0 right-0 z-10 flex items-center justify-center gap-3 text-xs text-white/85"
        style={{ pointerEvents: "auto" }}
      >
        <a
          href="/privacy"
          data-testid="footer-privacy-link"
          className="hover:text-white underline-offset-4 hover:underline transition-colors"
        >
          Privacy
        </a>
        <span className="opacity-50">·</span>
        <a
          href="/bookmarklet"
          data-testid="footer-bookmarklet-link"
          className="hover:text-white underline-offset-4 hover:underline transition-colors"
        >
          Share to PYS
        </a>
        <span className="opacity-50">·</span>
        <span>© {new Date().getFullYear()} PYS</span>
      </footer>
    </main>
  );
}
